/**
 * Background Rendering
 * 
 * Renders background images and effects.
 */

/**
 * Background type
 */
export type BackgroundType = 'image' | 'gradient' | 'checkerboard' | 'video' | 'majaku-privacy' | 'drawing' | 'paper';

/**
 * Background image state
 */
export interface BackgroundImageState {
  image: HTMLImageElement | null;
  loaded: boolean;
  startTime: number;
}

/**
 * Background video state
 */
export interface BackgroundVideoState {
  video: HTMLVideoElement | null;
  loaded: boolean;
  startTime: number;
}

/**
 * Gradient background configuration
 */
export interface GradientBackground {
  type: 'gradient';
  topColor: string;
  bottomColor: string;
}

/**
 * Checkerboard background configuration
 */
export interface CheckerboardBackground {
  type: 'checkerboard';
  color1: string;
  color2: string;
  tileSize: number;
}

/**
 * Background state (can be image, gradient, checkerboard, or video)
 */
export interface BackgroundState {
  type: BackgroundType;
  image?: BackgroundImageState;
  gradient?: GradientBackground;
  checkerboard?: CheckerboardBackground;
  video?: BackgroundVideoState;
}

/**
 * Create background image loader
 */
export function createBackgroundImage(src: string): BackgroundImageState {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.src = src;

  const state: BackgroundImageState = {
    image: img,
    loaded: false,
    startTime: performance.now(),
  };

  img.onload = () => {
    state.loaded = true;
  };

  img.onerror = () => {
  };

  return state;
}

/**
 * Create background video loader
 */
export function createBackgroundVideo(src: string): BackgroundVideoState {
  const video = document.createElement('video');
  video.crossOrigin = 'anonymous';
  video.src = src;
  video.loop = true;
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  
  // Append to DOM (hidden) to ensure playback works reliably in all browsers
  // This fixes issues where videos won't load or play if not attached to DOM
  video.style.position = 'absolute';
  video.style.width = '1px';
  video.style.height = '1px';
  video.style.opacity = '0';
  video.style.pointerEvents = 'none';
  video.style.zIndex = '-1000';
  document.body.appendChild(video);

  const state: BackgroundVideoState = {
    video: video,
    loaded: false,
    startTime: performance.now(),
  };

  const onLoaded = () => {
    if (state.loaded) return;
    state.loaded = true;
    // Start playing when loaded
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch((error) => {
        // If autoplay fails, we'll try again in the render loop
      });
    }
  };

  // Listen for multiple events to ensure we catch when it's ready
  video.addEventListener('loadeddata', onLoaded);
  video.addEventListener('canplay', onLoaded);
  video.addEventListener('playing', () => {
    // Ensure state is marked loaded if it starts playing
    if (!state.loaded) onLoaded();
  });

  video.addEventListener('error', (e) => {
  });

  // Try to load
  video.load();

  return state;
}

/**
 * Draw checkerboard background
 */
function drawCheckerboardBackground(
  ctx: CanvasRenderingContext2D,
  checkerboard: CheckerboardBackground,
  width: number,
  height: number
): void {
  const tileSize = checkerboard.tileSize;

  for (let y = 0; y < height; y += tileSize) {
    for (let x = 0; x < width; x += tileSize) {
      // Determine if this tile should be color1 or color2
      // Alternate based on both x and y position
      const tileX = Math.floor(x / tileSize);
      const tileY = Math.floor(y / tileSize);
      const isEven = (tileX + tileY) % 2 === 0;

      ctx.fillStyle = isEven ? checkerboard.color1 : checkerboard.color2;
      ctx.fillRect(x, y, tileSize, tileSize);
    }
  }
}

/**
 * Draw gradient background (skylight gradient)
 */
function drawGradientBackground(
  ctx: CanvasRenderingContext2D,
  gradient: GradientBackground,
  width: number,
  height: number
): void {
  // Create linear gradient from top to bottom
  const gradientObj = ctx.createLinearGradient(0, 0, 0, height);
  gradientObj.addColorStop(0, gradient.topColor);
  gradientObj.addColorStop(1, gradient.bottomColor);

  ctx.fillStyle = gradientObj;
  ctx.fillRect(0, 0, width, height);
}

/**
 * Draw background image with subtle animations
 */
function drawBackgroundImage(
  ctx: CanvasRenderingContext2D,
  bgState: BackgroundImageState,
  width: number,
  height: number
): void {
  if (!bgState.loaded || !bgState.image) {
    ctx.fillStyle = '#0e0f12';
    ctx.fillRect(0, 0, width, height);
    return;
  }

  const now = performance.now();
  const elapsed = (now - bgState.startTime) / 1000;

  // Subtle drift - slow sine waves with different periods
  const driftX = Math.sin(elapsed * 0.03) * 8 + Math.cos(elapsed * 0.04) * 5;
  const driftY = Math.cos(elapsed * 0.035) * 6 + Math.sin(elapsed * 0.045) * 4;

  // Subtle brightness variation
  const brightnessOffset = Math.sin(elapsed * 0.06) * 0.03 + Math.cos(elapsed * 0.09) * 0.02;

  // Subtle blur variation
  const blurBase = 18;
  const blurVariation = Math.sin(elapsed * 0.07) * 1.2 + Math.cos(elapsed * 0.11) * 0.8;
  const blurValue = blurBase + blurVariation;

  ctx.filter = `blur(${blurValue}px)`;
  ctx.globalAlpha = 0.95 + brightnessOffset;

  const scaleFactor = 1.15;
  const imgAspect = bgState.image.width / bgState.image.height;
  const canvasAspect = width / height;

  let drawWidth: number, drawHeight: number, drawX: number, drawY: number;

  if (imgAspect > canvasAspect) {
    drawHeight = height * scaleFactor;
    drawWidth = drawHeight * imgAspect;
    drawX = (width - drawWidth) / 2 + driftX;
    drawY = (height - drawHeight) / 2 + driftY;
  } else {
    drawWidth = width * scaleFactor;
    drawHeight = drawWidth / imgAspect;
    drawX = (width - drawWidth) / 2 + driftX;
    drawY = (height - drawHeight) / 2 + driftY;
  }

  ctx.drawImage(bgState.image, drawX, drawY, drawWidth, drawHeight);
  ctx.filter = 'none';
  ctx.globalAlpha = 1;
}

/**
 * Draw background video with subtle animations
 */
function drawBackgroundVideo(
  ctx: CanvasRenderingContext2D,
  bgState: BackgroundVideoState,
  width: number,
  height: number
): void {
  if (!bgState.loaded || !bgState.video) {
    ctx.fillStyle = '#0e0f12';
    ctx.fillRect(0, 0, width, height);
    return;
  }

  const now = performance.now();
  const elapsed = (now - bgState.startTime) / 1000;

  // Ensure video is playing
  if (bgState.video.paused && bgState.video.readyState >= 2) {
    bgState.video.play().catch(() => {
      // Ignore autoplay errors
    });
  }

  // Subtle drift - slow sine waves with different periods
  const driftX = Math.sin(elapsed * 0.03) * 8 + Math.cos(elapsed * 0.04) * 5;
  const driftY = Math.cos(elapsed * 0.035) * 6 + Math.sin(elapsed * 0.045) * 4;

  // Subtle brightness variation
  const brightnessOffset = Math.sin(elapsed * 0.06) * 0.03 + Math.cos(elapsed * 0.09) * 0.02;

  // Heavy blur for video backgrounds - much more blurry than image backgrounds
  const blurBase = 50;
  const blurVariation = Math.sin(elapsed * 0.07) * 3 + Math.cos(elapsed * 0.11) * 2;
  const blurValue = blurBase + blurVariation;

  ctx.filter = `blur(${blurValue}px)`;
  ctx.globalAlpha = 0.95 + brightnessOffset;

  const scaleFactor = 1.15;
  // Ensure video has valid dimensions
  const videoWidth = bgState.video.videoWidth || 1;
  const videoHeight = bgState.video.videoHeight || 1;
  const videoAspect = videoWidth / videoHeight;
  const canvasAspect = width / height;

  let drawWidth: number, drawHeight: number, drawX: number, drawY: number;

  if (videoAspect > canvasAspect) {
    drawHeight = height * scaleFactor;
    drawWidth = drawHeight * videoAspect;
    drawX = (width - drawWidth) / 2 + driftX;
    drawY = (height - drawHeight) / 2 + driftY;
  } else {
    drawWidth = width * scaleFactor;
    drawHeight = drawWidth / videoAspect;
    drawX = (width - drawWidth) / 2 + driftX;
    drawY = (height - drawHeight) / 2 + driftY;
  }

  ctx.drawImage(bgState.video, drawX, drawY, drawWidth, drawHeight);
  ctx.filter = 'none';
  ctx.globalAlpha = 1;
}

/**
 * Draw background (image, gradient, checkerboard, video, or majaku-privacy)
 * Note: majaku-privacy is handled separately via Three.js canvas, so this just draws a fallback
 */
export function drawBackground(
  ctx: CanvasRenderingContext2D,
  bgState: BackgroundState,
  width: number,
  height: number
): void {
  if (bgState.type === 'majaku-privacy') {
    // Majaku-privacy is rendered via separate Three.js canvas
    // Just draw a dark background as fallback (shouldn't be visible if Three.js is working)
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, width, height);
  } else if (bgState.type === 'checkerboard' && bgState.checkerboard) {
    drawCheckerboardBackground(ctx, bgState.checkerboard, width, height);
  } else if (bgState.type === 'gradient' && bgState.gradient) {
    drawGradientBackground(ctx, bgState.gradient, width, height);
  } else if (bgState.type === 'image' && bgState.image) {
    drawBackgroundImage(ctx, bgState.image, width, height);
  } else if (bgState.type === 'video' && bgState.video) {
    drawBackgroundVideo(ctx, bgState.video, width, height);
  } else {
    // Fallback to dark background
    ctx.fillStyle = '#0e0f12';
    ctx.fillRect(0, 0, width, height);
  }
}
